import { PlanCardLabels, romanNumeralsOneToTwenty } from 'src/lib/constants'

/**
 * A field that may carry several values, back to one value per entry.
 *
 * GET /plan declares division, divisionName and planElevation SCALAR, so app-plan
 * COMMA-JOINS a consolidated Approved Regional Plan's arrays on the way out — the
 * FE receives 'A4, C4, D4', never ['A4', 'C4', 'D4']. Anything that matches, maps
 * or renders one value at a time has to split them back out first: matching the
 * joined string as a whole silently never hits. Arrays (the shape the grid
 * projection keeps) are accepted too, so callers need not know which shape they
 * were handed, and a single-valued field yields exactly one entry.
 *
 * This lives here rather than beside allMultiValued/joinMultiValued in
 * src/components/utils so that src/lib/utilities can use it without the two
 * modules importing each other.
 */
export const splitMultiValued = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .filter((entry) => entry !== undefined && entry !== null && entry !== '')
    .flatMap((entry) => String(entry).split(','))
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')

export const toAlphaNumericCapitalCase = (str = '') => {
  const ans = str.toLowerCase().replace(/[^a-zA-Z0-9]/gi, '')
  return ans.charAt(0).toUpperCase() + ans.slice(1)
}

export const toAlphaNumericSpaceCapitalCase = (str = '') => {
  const ans = str.toLowerCase().replace(/[^a-zA-Z0-9\s]/gi, '')
  return ans.charAt(0).toUpperCase() + ans.slice(1)
}

export const toAlphaNumericSpaceTitleCase = (str = '') => {
  const ans = str.toLowerCase().replace(/[^a-zA-Z0-9\s]/gi, '')
  return ans
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const toAlphaNumericSpace = (str = '') => {
  const ans = str.replace(/[^a-zA-Z0-9\s]/gi, '')
  return ans.charAt(0).toUpperCase() + ans.slice(1)
}
export const toAlphaNumericSpaceApostrophe = (str = '') => {
  return str.replace(/[^a-zA-Z0-9\s']/gi, '')
}

export const toSentenceCase = (str = '') => {
  if (str) {
    const CAPITALIZE_FIRST_LETTERS = /([A-Z])/g
    return str
      .replace(CAPITALIZE_FIRST_LETTERS, ' $1')
      .replace(/^./, (match) => match.toUpperCase())
  }
  return str
}

export const toTitleCase = (str: string) => {
  const WORD_SEPARATORS = /(-|\/|\\)/
  const IS_SEPARATOR = /^[-/\\]$/

  const capitalizeWord = (word: string) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

  return str
    .split(' ')
    .map((word) =>
      word
        .split(WORD_SEPARATORS)
        .map((part) => (IS_SEPARATOR.test(part) ? part : capitalizeWord(part)))
        .join('')
    )
    .join(' ')
}

/**
 * Capitalizes the first letter of each word and uppercases whole roman
 * numerals, but leaves the rest of each word exactly as typed so the user
 * stays in control of interior casing (e.g. "McCartny").
 */
export const capitalizeWordsExceptRomanNumerals = (str: string) => {
  const words = str.split(' ')
  const newWords = words.map((word) => {
    if (romanNumeralsOneToTwenty.includes(word.toUpperCase())) {
      return word.toUpperCase()
    }
    return word.charAt(0).toUpperCase() + word.slice(1)
  })
  return newWords.join(' ')
}

export const pluralize = (word: string, count: number): string => {
  if (word === PlanCardLabels.COMMUNITY) {
    return count === 1 ? word : PlanCardLabels.COMMUNITIES
  }
  return count === 1 ? word : `${word}s`
}

export const toDateString = (isoDate: string): string => {
  return new Date(isoDate).toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export const formatNumber = (value?: number): string => {
  return value?.toLocaleString('en-US') || '-'
}
